import { createContext, useContext, type PropsWithChildren } from "react";

import { DiscardRenewalModal } from "@components/DiscardRenewalModal";
import { MarkRenewalAsPaidModal } from "@components/MarkRenewalAsPaidModal";
import { MarkRenewalAsUnpaidModal } from "@components/MarkRenewalAsUnpaidModal";

import { RenewalWithCheckReceivalFragment } from "@covertree/agent-portal-graphql";
import { useRenewalDiscard } from "./hooks/useRenewalDiscard";
import { useRenewalCheckReceivalAction } from "./hooks/useRenewalCheckReceivalAction";

interface RenewalActionsProviderState {
  showRenewalDiscardModal(
    renewalLocator: string,
    shouldRefetchListAfterDiscard?: boolean
  ): void;
  showMarkAsPaidModal(renewal: RenewalWithCheckReceivalFragment): void;
  showMarkAsUnpaidModal(renewal: RenewalWithCheckReceivalFragment): void;
}

const initialState: RenewalActionsProviderState = {
  showRenewalDiscardModal: () => {},
  showMarkAsPaidModal: () => {},
  showMarkAsUnpaidModal: () => {},
};

const RenewalActionsContext =
  createContext<RenewalActionsProviderState>(initialState);

export const useRenewalActions = () => useContext(RenewalActionsContext);

export function RenewalActionsProvider({ children }: PropsWithChildren) {
  const {
    renewalLocatorToDiscard,
    showRenewalDiscardModal,
    shouldRefetchListAfterDiscard,
    closeRenewalDiscardModal,
  } = useRenewalDiscard();

  const {
    markAsPaidOpen,
    markAsUnpaidOpen,
    renewalToManageCheckReceival,
    showMarkAsPaidModal,
    showMarkAsUnpaidModal,
    closeRenewalCheckReceivalModal,
  } = useRenewalCheckReceivalAction();

  return (
    <RenewalActionsContext.Provider
      value={{
        showRenewalDiscardModal,
        showMarkAsPaidModal,
        showMarkAsUnpaidModal,
      }}>
      {children}
      <DiscardRenewalModal
        closeModal={closeRenewalDiscardModal}
        renewalLocator={renewalLocatorToDiscard}
        shouldRefetchListAfterDiscard={shouldRefetchListAfterDiscard}
      />
      {markAsPaidOpen && (
        <MarkRenewalAsPaidModal
          open={markAsPaidOpen}
          renewal={renewalToManageCheckReceival}
          onClose={closeRenewalCheckReceivalModal}
        />
      )}
      <MarkRenewalAsUnpaidModal
        open={markAsUnpaidOpen}
        renewalLocator={renewalToManageCheckReceival?.renewalLocator}
        onClose={closeRenewalCheckReceivalModal}
      />
    </RenewalActionsContext.Provider>
  );
}
